import { useState, useRef, useEffect } from "react";

const OR_KEY = "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";
const OR_URL = "https://openrouter.ai/api/v1/chat/completions";
export default function MusicAI({ song, onClose }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: song
        ? `Hi! I'm MyMusic AI 🎵 I can see you're listening to "${song.trackName}" by ${song.artistName}. Ask me anything about this song or anything else!`
        : `Hi! I'm MyMusic AI 🎵 Ask me anything — song meanings, lyrics translation, music theory, recommendations, or any topic!`
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function sendMessage() {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const systemPrompt = song
        ? `You are MyMusic AI, a smart friendly assistant inside the MyMusic app. The user is listening to "${song.trackName}" by "${song.artistName}". Answer anything — about this song, music, or any topic. Speak multiple languages including English, Myanmar, Korean, Japanese. Be warm, helpful, use emojis. Keep responses under 300 words.`
        : `You are MyMusic AI, a smart friendly assistant inside the MyMusic app. Help with anything — music, lyrics, translation, songwriting, general knowledge, life advice. Speak multiple languages including English, Myanmar (Burmese), Korean, Japanese, Hindi. Be warm, helpful, use emojis. Keep responses under 300 words.`;

      const conversationHistory = messages
        .filter((_, i) => i > 0)
        .map(m => ({
          role: m.role === "assistant" ? "model" : "user",
          parts: [{ text: m.content }]
        }));

const response = await fetch(OR_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${OR_KEY}`,
          "HTTP-Referer": "https://mymusicstream.online",
          "X-Title": "MyMusic AI"
        },
        body: JSON.stringify({
         model: "openai/gpt-oss-120b:free",
          max_tokens: 500,
          messages: [
            { role: "system", content: systemPrompt },
            ...messages.filter((_, i) => i > 0).map(m => ({
              role: m.role === "assistant" ? "assistant" : "user",
              content: m.content
            })),
            { role: "user", content: userMsg }
          ]
        })
      });

      const data = await response.json();
      const reply = data.choices?.[0]?.message?.content || data.error?.message || "Sorry, I couldn't process that. Try again!";
        
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, something went wrong. Please try again!" }]);
    }
    setLoading(false);
  }

  const quickQuestions = song ? [
    `What is "${song.trackName}" about?`,
    "What's the mood of this song?",
    "Tell me about the artist",
    "Translate the title to Myanmar",
  ] : [
    "Recommend a sad song 🎵",
    "Translate something to Myanmar 🇲🇲",
    "Help me write lyrics ✍️",
    "Tell me a fun fact 💡",
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 502, display: "flex", flexDirection: "column", fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap'); @keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }`}</style>

      {/* Header */}
      <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid #1a1a1a", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <button onClick={onClose} style={{ background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg, #1db954, #0a8f3c)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "none", stroke: "#fff", strokeWidth: 1.8 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><circle cx="9" cy="10" r="1" fill="#fff"/><circle cx="12" cy="10" r="1" fill="#fff"/><circle cx="15" cy="10" r="1" fill="#fff"/></svg>
        </div>
        <div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800 }}>MyMusic AI</div>
          <div style={{ fontSize: 11, color: "#1db954" }}>● Always here for you</div>
        </div>
        {song && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, background: "#111", borderRadius: 50, padding: "6px 12px", border: "1px solid #1a1a1a", maxWidth: 140 }}>
            <img src={song.artworkUrl100} alt="" style={{ width: 24, height: 24, borderRadius: 4, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{song.trackName}</span>
          </div>
        )}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", gap: 8, alignItems: "flex-end" }}>
            {msg.role === "assistant" && (
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, #1db954, #0a8f3c)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: "none", stroke: "#fff", strokeWidth: 2 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
            )}
            <div style={{ maxWidth: "75%", padding: "10px 14px", borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: msg.role === "user" ? "#1db954" : "#1a1a1a", color: msg.role === "user" ? "#000" : "#fff", fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, #1db954, #0a8f3c)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: "none", stroke: "#fff", strokeWidth: 2 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </div>
            <div style={{ padding: "12px 16px", borderRadius: "18px 18px 18px 4px", background: "#1a1a1a", display: "flex", gap: 4, alignItems: "center" }}>
              {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#555", animation: `bounce 1s ease infinite ${i * 0.2}s` }} />)}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Quick questions */}
      {messages.length <= 1 && (
        <div style={{ padding: "0 16px 12px", display: "flex", gap: 8, overflowX: "auto", flexShrink: 0 }}>
          {quickQuestions.map((q, i) => (
            <button key={i} onClick={() => setInput(q)} style={{ padding: "8px 14px", borderRadius: 50, border: "1px solid #2a2a2a", background: "#111", color: "#ccc", fontSize: 12, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ padding: "12px 16px 16px", borderTop: "1px solid #1a1a1a", display: "flex", gap: 10, alignItems: "center", flexShrink: 0, position: "sticky", bottom: 0, background: "#000" }}>
        <input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask me anything..." onKeyDown={e => e.key === "Enter" && sendMessage()}
          style={{ flex: 1, padding: "12px 16px", borderRadius: 50, border: "1px solid #2a2a2a", background: "#111", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none" }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()} style={{ width: 44, height: 44, borderRadius: "50%", border: "none", background: loading || !input.trim() ? "#222" : "#1db954", display: "flex", alignItems: "center", justifyContent: "center", cursor: loading ? "not-allowed" : "pointer", flexShrink: 0 }}>
          <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "none", stroke: loading || !input.trim() ? "#555" : "#000", strokeWidth: 2 }}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
      </div>
    </div>
  );
}